/**
 * judgeQueue — Durable, idempotent write queue for competition judge events.
 *
 * Purpose: a judge's tap (Fall / Zone / Top) MUST NOT be lost even if the
 * network drops mid-competition, the tab is refreshed, the PWA is killed by
 * iOS, or the device dies. This module is the durability layer between the
 * judge UI and Firestore.
 *
 * Storage backends (auto-selected):
 *   1. IndexedDB — primary; survives refresh and PWA kill; large capacity.
 *      We use a single object store with NO Web Locks API, NO multi-tab
 *      coordination — this is what made Firestore's persistentMultipleTabManager
 *      deadlock on iPad Safari (commit 6f0ac62). A plain single-store IDB is
 *      stable on every browser including iPad Safari/Chrome iOS.
 *   2. localStorage — fallback when IDB is unavailable (private mode, exotic
 *      WebViews). Small capacity but durable across refreshes.
 *
 * Idempotency:
 *   Each event gets a deterministic ID `{actorUid}_{tsMs}_{seq}`. On flush we
 *   call `setDoc(audit/{id}, payload, { merge: true })`. If the original write
 *   succeeded but the ack was lost, a retry overwrites the same doc with the
 *   same data — no duplicates, no corruption.
 *
 * Ordering:
 *   `serverTimestamp()` is used for `createdAt` so the Cloud Function
 *   `recomputeAttemptFromAudit` orders events consistently across judges. We
 *   also store `tappedAt` (client wall-clock at the moment of the tap) for
 *   forensics and possible future client-truth ordering.
 *
 * Lifecycle:
 *   enqueueJudgeEvent(payload)  → write to local store, optimistic UI returns
 *   flushJudgeQueue()           → background: setDoc each pending event
 *   online event                → auto-flush
 *   successful ack              → remove from queue
 *   failure                     → exponential backoff retry (capped at 30s)
 */

import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { withTimeout, WRITE_TIMEOUT_MS } from "./promiseTimeout";
import { buildJudgeEventAudit } from "./judgeAuditDoc";
import { isClientOutdated } from "../hooks/useClientCompat";
import { localStore } from "./safeStorage";

const DB_NAME = "sendly-judge-queue";
const STORE = "events";
const VERSION = 1;
const LS_FALLBACK_KEY = "sendly.judgeQueue.fallback";
// Quarantined items (from a previously-logged-in judge that never came back)
// are dropped after this age — assumed they'll never sync. 7 days lets a
// judge resume the next day after a multi-day comp without losing data, but
// prevents the queue from growing indefinitely on a shared iPad.
const STALE_ITEM_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let _dbPromise = null;
let _seq = 0;
let _backendCache = null; // "idb" | "ls" | null (unknown)
const _subscribers = new Set();
let _flushing = false;
let _backoffMs = 1000;
// An undo races the flush: the flush works from a snapshot of the store, and a
// write in flight cannot be called back. So the two decide, synchronously, who
// owns an item: the flush CLAIMS it right before writing it (an undo then takes
// the revoke / re-save path), and an undo that comes first marks it DROPPED (the
// flush then skips it). Without this, a tap undone while its write was already
// out — or still in the flush's snapshot — landed on the server anyway: a phantom
// attempt the judge believed was gone.
const _claimed = new Set();
const _dropped = new Set();

// ---- IndexedDB wrapper (single-store, no Web Locks) ----
function _openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("no-indexeddb"));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const idb = req.result;
      if (!idb.objectStoreNames.contains(STORE)) {
        idb.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("idb-open-failed"));
    req.onblocked = () => reject(new Error("idb-blocked"));
  });
  _dbPromise.catch(() => { _dbPromise = null; });
  return _dbPromise;
}

function _idbTx(mode, fn) {
  return _openDb().then((idb) => new Promise((resolve, reject) => {
    const tx = idb.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("idb-tx-abort"));
  }));
}

async function _idbPut(item) {
  return _idbTx("readwrite", (store) => store.put(item));
}
async function _idbDelete(id) {
  return _idbTx("readwrite", (store) => store.delete(id));
}
async function _idbGetAll() {
  return _idbTx("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  }));
}

// ---- localStorage fallback ----
function _lsRead() {
  return localStore.getJSON(LS_FALLBACK_KEY, []);
}
function _lsWrite(items) {
  localStore.setJSON(LS_FALLBACK_KEY, items);
}

// ---- Backend selection (lazy, cached) ----
async function _backend() {
  if (_backendCache) return _backendCache;
  try {
    await _openDb();
    _backendCache = "idb";
  } catch {
    _backendCache = "ls";
  }
  return _backendCache;
}

async function _putItem(item) {
  const be = await _backend();
  if (be === "idb") return _idbPut(item);
  const items = _lsRead();
  const idx = items.findIndex((it) => it.id === item.id);
  if (idx >= 0) items[idx] = item; else items.push(item);
  _lsWrite(items);
}

async function _getAll() {
  const be = await _backend();
  if (be === "idb") return _idbGetAll();
  return _lsRead();
}

async function _remove(id) {
  const be = await _backend();
  if (be === "idb") return _idbDelete(id);
  _lsWrite(_lsRead().filter((it) => it.id !== id));
}

// ---- Event ID construction ----
/**
 * Build a deterministic, lex-sortable event ID.
 * Format: {actorUid}_{tsMs padded 13}_{seq padded 3}
 * The padding ensures alphabetical and chronological order match.
 */
export function buildEventId(actorUid, nowMs = Date.now()) {
  _seq = (_seq + 1) % 1000;
  const padTs = String(nowMs).padStart(13, "0");
  const padSeq = String(_seq).padStart(3, "0");
  return `${actorUid}_${padTs}_${padSeq}`;
}

// ---- Public API ----

/**
 * Enqueue a judge event (fall / zone / top) for durable, idempotent write.
 * Returns the audit doc ID immediately so the UI can show optimistic state.
 *
 * payload shape:
 *   {
 *     gymId, compId, attemptId,         // path identifiers
 *     type: "fall" | "zone" | "top",
 *     actorUid, actorRole,              // who tapped
 *     uid, routeId,                     // climber + route
 *     groupId, sessionId, snapshot,     // context (snapshot optional)
 *   }
 */
export async function enqueueJudgeEvent(payload) {
  if (!payload?.actorUid || !payload?.gymId || !payload?.compId || !payload?.attemptId) {
    throw new Error("judgeQueue.enqueue: missing required field");
  }
  const id = buildEventId(payload.actorUid);
  const item = {
    id,
    op: "event",
    payload: { ...payload, eventId: id, tappedAt: Date.now() },
    enqueuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await _putItem(item);
  _notify();
  // Fire-and-forget: try to flush right away. If offline, this is a no-op
  // and the next online/visibility event will retry. Pass retryBlocked
  // because user activity (= a fresh tap / save) is itself a wake-up
  // signal: if the rules gate just opened (organizer re-opened attempt
  // recording), older blocked items should drain alongside the new one
  // instead of waiting for the 30 s periodic retry.
  flushJudgeQueue({ retryBlocked: true }).catch(() => {});
  return id;
}

/**
 * Enqueue a competitor JAM-mode self-save (zone / top attempt-count snapshot
 * recorded by the climber themselves). Both writes — the aggregate
 * `attempts/{aid}` doc AND a `jam_save` audit entry — go through this queue
 * so the data survives tab refresh, PWA kill, and offline. Unlike judge
 * events, JAM saves do NOT support delegation: only the original competitor
 * (matching `auth.currentUser.uid`) can flush these. If a different user is
 * signed in when the queue runs, JAM items remain pending until the
 * original competitor returns (or stale-prune drops them after 7 days).
 *
 * payload shape:
 *   {
 *     gymId, compId, attemptId,
 *     uid, routeId, groupId, sessionId,
 *     actorUid,                  // === uid for a true JAM save
 *     actorRole: "competitor",
 *     snapshot: { zoneAttempts, zoneReached, topAttempts, topped },
 *     beforeSnapshot?,           // optional pre-edit aggregate for forensics
 *   }
 */
export async function enqueueJamSave(payload) {
  if (!payload?.actorUid || !payload?.gymId || !payload?.compId || !payload?.attemptId || !payload?.snapshot) {
    throw new Error("judgeQueue.enqueueJamSave: missing required field");
  }
  const id = buildEventId(payload.actorUid);
  const item = {
    id,
    op: "jam",
    payload: { ...payload, eventId: id, tappedAt: Date.now() },
    enqueuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await _putItem(item);
  _notify();
  // retryBlocked: same rationale as enqueueJudgeEvent — a fresh JAM save is
  // also a wake-up signal; older blocked items piggyback on it.
  flushJudgeQueue({ retryBlocked: true }).catch(() => {});
  return id;
}

/**
 * Enqueue a revoke (undo) of a previously-written audit entry.
 * `auditId` MUST be the deterministic ID returned by enqueueJudgeEvent.
 */
export async function enqueueJudgeRevoke({ gymId, compId, attemptId, auditId, actorUid }) {
  if (!auditId || !gymId || !compId || !attemptId || !actorUid) {
    throw new Error("judgeQueue.revoke: missing required field");
  }
  const id = `revoke_${auditId}`;
  const item = {
    id,
    op: "revoke",
    payload: { gymId, compId, attemptId, auditId, actorUid },
    enqueuedAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await _putItem(item);
  _notify();
  // retryBlocked: a revoke is user activity too; piggyback blocked retries.
  flushJudgeQueue({ retryBlocked: true }).catch(() => {});
  return id;
}

/**
 * Detect a Firestore rules rejection (vs. a transient network error). When
 * we hit one, the write will keep failing forever until human action
 * changes the rule outcome — typical case: the competition's `status` was
 * flipped to `closing` / `closed` while the event sat in the queue, so the
 * audit-create rule blocks the write. We mark such items as `blocked` and
 * stop the retry loop until either the original cause is lifted (the
 * organizer re-opens attempt registration) or the user discards them
 * deliberately.
 */
function _isPermissionDenied(err) {
  if (!err) return false;
  if (err.code === "permission-denied") return true;
  const msg = String(err?.message || "").toLowerCase();
  return msg.includes("permission-denied") || msg.includes("permission denied")
    || msg.includes("missing or insufficient permissions");
}

/**
 * Attempt to flush all pending events to Firestore. Safe to call concurrently;
 * a single in-flight flush is enforced via `_flushing`.
 *
 * Items whose `actorUid` doesn't match the current `auth.currentUser.uid`
 * are still flushed — they take the DELEGATED FLUSH path, which adds a
 * `delegatedFlushBy: <current uid>` field so the audit log honestly records
 * who tapped (`actorUid`) and who uploaded (`delegatedFlushBy`). The
 * Firestore rule's clause D requires both the current writer and the
 * original `actorUid` to be registered judges (or admin / organizer for
 * the writer side). If neither precondition holds, the write fails and
 * the item retries until someone with sufficient privilege signs in.
 *
 * Items already flagged `blocked` (a previous flush hit a rules rejection)
 * are skipped by default — retrying them on every flush would burn cycles
 * for nothing because the rule outcome doesn't change between back-to-back
 * attempts. Pass `{ retryBlocked: true }` from a "wake-up" event (network
 * online, visibility resume, auth state change, fresh boot) so the queue
 * can pick them up if the organizer re-opened the comp in the meantime.
 */
export async function flushJudgeQueue(opts = {}) {
  // Below the live compatibility level (useClientCompat): the backend may no
  // longer accept this build's write shape, and the mid-session modal does not
  // reload. Leave every item in the store untouched — the NEW build drains
  // them after the update, so the item shape is a contract (judgeQueue.test.js
  // pins that today's stored items still flush).
  if (isClientOutdated()) return { skipped: true, reason: "client-outdated" };
  if (_flushing) return { skipped: true };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { skipped: true, reason: "offline" };
  }
  const currentUid = auth?.currentUser?.uid || null;
  if (!currentUid) {
    // No auth — every write would fail the rules anyway. Wait for login.
    return { skipped: true, reason: "no-auth" };
  }
  const retryBlocked = !!opts.retryBlocked;
  _flushing = true;
  let okCount = 0;
  let failCount = 0;
  let delegated = 0;
  let blockedNow = 0;
  try {
    // Drop stale items (older than 7 days) before processing. They're
    // assumed unrecoverable — the original actor never came back and no
    // delegate ever picked them up either. Blocked items are excluded
    // from stale-pruning (they represent real climbs awaiting human
    // intervention; we don't want to silently delete them).
    await _pruneStaleItems();
    const items = await _getAll();
    if (items.length === 0) {
      _backoffMs = 1000;
      return { okCount: 0, failCount: 0, delegated: 0, blockedNow: 0 };
    }
    items.sort((a, b) => a.enqueuedAt - b.enqueuedAt);
    for (const item of items) {
      // Skip rules-blocked items on the inner retry path. Wake-up events
      // pass retryBlocked=true to give the comp organizer a chance to have
      // re-opened attempt registration since the last try.
      if (item.blocked && !retryBlocked) continue;

      const isDelegated = item.payload?.actorUid !== currentUid;
      // JAM saves don't support delegation — only the original competitor
      // can sign their own attempts. If a different user is currently
      // signed in, skip the item (it stays pending until the original
      // competitor returns or the 7-day stale-prune drops it).
      if (item.op === "jam" && isDelegated) continue;
      // An undo came first: the item never leaves the device.
      if (_dropped.has(item.id)) continue;
      _claimed.add(item.id);
      try {
        await _writeOne(item, currentUid);
        await _remove(item.id);
        okCount++;
        if (isDelegated) delegated++;
      } catch (err) {
        item.attempts = (item.attempts || 0) + 1;
        item.lastError = String(err?.message || err);
        if (_isPermissionDenied(err)) {
          // Rules rejection — the comp is closed/closing, the judge was
          // unregistered, or some other policy gate is shut. Don't keep
          // hammering it; mark blocked and surface it to the UI so the
          // user can either ask the organizer to re-open the gate (then
          // a future wake-up retry will succeed) or explicitly discard
          // the lost work.
          if (!item.blocked) blockedNow++;
          item.blocked = true;
          item.blockedReason = err?.code || "permission-denied";
          item.blockedAt = item.blockedAt || Date.now();
          await _putItem(item);
          // Don't break — try the rest of the queue. Other items might be
          // for different comps that are still open. Subsequent
          // permission-denied items also get blocked individually.
          failCount++;
          continue;
        }
        await _putItem(item);
        failCount++;
        // Stop the batch early on a transient failure — a flaky network
        // usually means subsequent writes will fail too, and we don't want
        // to burn through retries on every queued event.
        break;
      } finally {
        _claimed.delete(item.id);
      }
    }
    _notify();
    // Only schedule a backoff retry if we had transient failures. A
    // freshly-blocked item is not "retry me" — it needs a wake-up event.
    const transientFail = failCount > blockedNow;
    if (transientFail) {
      _backoffMs = Math.min(_backoffMs * 2, 30000);
      setTimeout(() => { flushJudgeQueue().catch(() => {}); }, _backoffMs);
    } else if (okCount > 0) {
      _backoffMs = 1000;
    }
    return { okCount, failCount, delegated, blockedNow };
  } finally {
    _flushing = false;
  }
}

/**
 * Drop items older than STALE_ITEM_MAX_AGE_MS. Called automatically at the
 * start of each flush. Items are normally tied to a specific judge whose
 * device has been online recently; an item this old means the device hasn't
 * synced for a week, which is a sign the data is unrecoverable.
 *
 * BLOCKED items are deliberately excluded from stale-pruning. A `blocked`
 * item represents a real climb whose Firestore write was rejected by the
 * rules (typically because the comp was closed before the event flushed) —
 * the user gets a banner about it and the organizer can either re-open
 * registration (auto-retry succeeds) or the user can explicitly discard
 * via `discardBlockedJudgeEvents()`. Silently deleting them at 7 days
 * would erase data the user is still potentially waiting to recover.
 */
async function _pruneStaleItems() {
  const cutoff = Date.now() - STALE_ITEM_MAX_AGE_MS;
  const items = await _getAll();
  let pruned = 0;
  for (const it of items) {
    if (it.blocked) continue;
    if ((it.enqueuedAt || 0) < cutoff) {
      await _remove(it.id);
      pruned++;
    }
  }
  if (pruned > 0) _notify();
  return pruned;
}

async function _writeOne(item, currentUid) {
  // Belt-and-suspenders offline check. flushJudgeQueue() also bails before
  // calling us if `navigator.onLine === false`, but the network can drop
  // BETWEEN the flush precheck and this individual write — and Firestore
  // would silently buffer a pending write forever in that case. Throwing
  // here makes the queue treat it as a normal failure and reschedule.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw new Error("offline");
  }
  const p = item.payload;
  if (item.op === "revoke") {
    const ref = doc(db, "gyms", p.gymId, "competitions", p.compId, "attempts", p.attemptId, "audit", p.auditId);
    // For revokes, the rule allows the writer to be the original actor OR
    // the delegate. We always pass `revokedBy: currentUid` so the audit
    // log records the actual click-er; the rule's update clauses then
    // accept it on either of those two paths.
    await withTimeout(setDoc(ref, {
      revoked: true,
      revokedAt: serverTimestamp(),
      revokedBy: currentUid || p.actorUid,
    }, { merge: true }), WRITE_TIMEOUT_MS, "revoke setDoc");
    return;
  }
  if (item.op === "jam") {
    // Competitor JAM-mode self-save — two writes per item:
    //   1. Aggregate doc (the canonical state of this climber+route)
    //   2. Audit entry of type `jam_save` (forensic log of who saved when)
    // Both writes are idempotent: same deterministic eventId on the audit
    // doc, and setDoc(merge:true) on the aggregate writes the same fields
    // each retry. Order matters less than correctness — if step 1 succeeds
    // but step 2 fails, the next retry replays both safely.
    const s = p.snapshot || {};
    const aggRef = doc(db, "gyms", p.gymId, "competitions", p.compId, "attempts", p.attemptId);
    await withTimeout(setDoc(aggRef, {
      uid: p.uid,
      routeId: p.routeId,
      groupId: p.groupId || "",
      sessionId: p.sessionId || "",
      zoneAttempts: s.zoneAttempts || 0,
      zoneReached: s.zoneReached || false,
      topAttempts: s.topAttempts || 0,
      topped: s.topped || false,
      registeredBy: p.actorUid,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    }, { merge: true }), WRITE_TIMEOUT_MS, "jam aggregate setDoc");

    const auditRef = doc(db, "gyms", p.gymId, "competitions", p.compId, "attempts", p.attemptId, "audit", p.eventId);
    await withTimeout(setDoc(auditRef, {
      type: "jam_save",
      actorUid: p.actorUid,
      actorRole: p.actorRole || "competitor",
      createdAt: serverTimestamp(),
      uid: p.uid,
      routeId: p.routeId,
      groupId: p.groupId || "",
      sessionId: p.sessionId || "",
      compId: p.compId,
      snapshot: {
        zoneAttempts: s.zoneAttempts || 0,
        zoneReached: s.zoneReached || false,
        topAttempts: s.topAttempts || 0,
        topped: s.topped || false,
      },
      ...(p.beforeSnapshot ? { beforeSnapshot: p.beforeSnapshot } : {}),
      tappedAt: p.tappedAt || null,
    }, { merge: true }), WRITE_TIMEOUT_MS, "jam audit setDoc");
    return;
  }
  // op === "event"
  // Delegated flush: when the original tapper isn't the user currently
  // signed in (judge A tapped offline → judge B took over the iPad before
  // the queue drained), include `delegatedFlushBy` so the rule lets it
  // through and the audit log preserves the chain of custody (`actorUid`
  // = who tapped, `delegatedFlushBy` = who uploaded).
  const isDelegated = currentUid && currentUid !== p.actorUid;
  const ref = doc(db, "gyms", p.gymId, "competitions", p.compId, "attempts", p.attemptId, "audit", p.eventId);
  await withTimeout(setDoc(ref, buildJudgeEventAudit(p, {
    createdAt: serverTimestamp(),
    delegatedFlushBy: isDelegated ? currentUid : null,
  }), { merge: true }), WRITE_TIMEOUT_MS, "judge audit setDoc");
}

/**
 * Snapshot of currently pending items (for UI badges, debug, etc.).
 */
export async function getPendingJudgeEvents() {
  return _getAll();
}

/**
 * Remove a pending event by its audit ID, if it hasn't been flushed yet.
 * Returns true if the event was found and removed, false otherwise.
 *
 * Used by undoJudgeEvent: if a judge taps "undo" before the original event
 * was flushed to Firestore, we can simply drop it from the queue — no need
 * to write anything to Firestore at all.
 */
export async function removePendingEvent(auditId) {
  return _dropPending(auditId, "event");
}

/**
 * Drop a not-yet-sent item of `op`, or report that it is too late (false): it is
 * being written right now (claimed by the running flush) or it is gone already.
 * The claim check and the drop mark happen before the first await, so a flush
 * can never write an item an undo dropped, nor an undo drop one being written.
 */
async function _dropPending(id, op) {
  if (!id || _claimed.has(id)) return false;
  _dropped.add(id);
  const items = await _getAll();
  const target = items.find((it) => it.id === id && it.op === op);
  if (!target) {
    _dropped.delete(id);
    return false;
  }
  await _remove(id);
  _notify();
  return true;
}

/**
 * Remove a pending JAM self-save by its queue ID, if it hasn't flushed yet.
 * Returns true if found and removed, false otherwise.
 *
 * Mirror of `removePendingEvent` for the competitor self-save flow: when the
 * climber taps "undo" on the post-save toast BEFORE the queue drained, we can
 * simply drop the queued item — the aggregate Firestore write never happened,
 * so the optimistic UI merge reverts to the prior server state with no
 * Firestore round-trip. (If it already flushed, the caller re-saves the prior
 * snapshot instead — see `undoJamSave` in useCompetition.)
 */
export async function removePendingJamSave(eventId) {
  return _dropPending(eventId, "jam");
}

/**
 * Subscribe to queue changes. Callback receives the full pending array.
 * Returns an unsubscribe function.
 */
export function subscribeJudgeQueue(cb) {
  _subscribers.add(cb);
  _getAll().then((items) => cb(items)).catch(() => cb([]));
  return () => { _subscribers.delete(cb); };
}

function _notify() {
  _getAll().then((items) => {
    for (const cb of _subscribers) {
      try { cb(items); } catch { /* subscriber bug; isolate */ }
    }
  }).catch(() => {});
}

/**
 * Snapshot of items that the Firestore rules permanently rejected (currently:
 * comp closed / closing, judge unregistered, similar). These won't drain on
 * their own — the user has to either ask the organizer to re-open attempt
 * registration (next wake-up flush picks them up) or call
 * `discardBlockedJudgeEvents()` to throw them away.
 */
export async function getBlockedJudgeEvents() {
  const items = await _getAll();
  return items.filter((it) => it.blocked);
}

/**
 * Drop every blocked item from the queue. Destructive — only call when the
 * user explicitly opts to throw away the rejected work (e.g. via the X
 * button on the warning banner). Returns the number of items dropped.
 */
export async function discardBlockedJudgeEvents() {
  const items = await _getAll();
  const blocked = items.filter((it) => it.blocked);
  for (const it of blocked) await _remove(it.id);
  if (blocked.length > 0) _notify();
  return blocked.length;
}

/**
 * Install global listeners that auto-flush on network/visibility/auth
 * changes. Call once at app boot. Returns a cleanup function.
 *
 * "Wake-up" events (online, visibility resume, auth state change, boot)
 * pass `retryBlocked: true` so previously-blocked items get a fresh
 * attempt — the typical recovery flow is the organizer re-opening
 * attempt registration in between, and these are the natural moments the
 * client notices that change.
 */
export function installJudgeQueueRunner() {
  if (typeof window === "undefined") return () => {};
  const onOnline = () => { _backoffMs = 1000; flushJudgeQueue({ retryBlocked: true }).catch(() => {}); };
  const onVis = () => { if (!document.hidden) flushJudgeQueue({ retryBlocked: true }).catch(() => {}); };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVis);

  // Auth state change: when a user signs in, immediately attempt a flush —
  // events that were quarantined while they were logged out will now match
  // the current auth.uid and can drain. Logout (user === null) intentionally
  // does NOT flush (writes would fail) — those items stay queued for the
  // next sign-in of the same user.
  let unsubAuth = () => {};
  try {
    if (auth?.onAuthStateChanged) {
      unsubAuth = auth.onAuthStateChanged((user) => {
        if (user) {
          _backoffMs = 1000;
          flushJudgeQueue({ retryBlocked: true }).catch(() => {});
        }
      });
    }
  } catch { /* auth not available */ }

  // Boot-time flush: there may be leftover items from a previous tab session.
  // Defer past first paint so we don't compete with the initial Firestore
  // listener attach burst.
  setTimeout(() => { flushJudgeQueue({ retryBlocked: true }).catch(() => {}); }, 2500);

  // Periodic retry while there ARE blocked items in the queue. Without this
  // the banner could stay forever: a judge whose tab is online and idle
  // when the organizer re-opens attempt registration would otherwise have
  // to refresh the page / tab-switch / lose+regain Wi-Fi to trigger one
  // of the wake-up events. 30 s is short enough that the recovery feels
  // automatic and long enough that we don't hammer Firestore with
  // permission-denied retries.
  let blockedInterval = null;
  const unsubBlockedWatch = (() => {
    const cb = (items) => {
      const hasBlocked = Array.isArray(items) && items.some((it) => it && it.blocked);
      if (hasBlocked && !blockedInterval) {
        blockedInterval = setInterval(() => {
          flushJudgeQueue({ retryBlocked: true }).catch(() => {});
        }, 30000);
      } else if (!hasBlocked && blockedInterval) {
        clearInterval(blockedInterval);
        blockedInterval = null;
      }
    };
    _subscribers.add(cb);
    _getAll().then(cb).catch(() => {});
    return () => { _subscribers.delete(cb); };
  })();

  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVis);
    if (typeof unsubAuth === "function") unsubAuth();
    if (blockedInterval) { clearInterval(blockedInterval); blockedInterval = null; }
    unsubBlockedWatch();
  };
}

// ---- Test helpers (no-op in production) ----
/** @internal — testing only. Clears the queue. */
export async function __resetJudgeQueueForTests() {
  _backendCache = null;
  _dbPromise = null;
  _seq = 0;
  _flushing = false;
  _backoffMs = 1000;
  _claimed.clear();
  _dropped.clear();
  try {
    if (typeof indexedDB !== "undefined") {
      await new Promise((resolve) => {
        const req = indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
      });
    }
  } catch { /* ignore */ }
  localStore.remove(LS_FALLBACK_KEY);
}
